import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Target, Trash2, CalendarClock, ListTodo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusBadge, EmptyState, PersonAvatar } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useEmployees } from "@/lib/useEmployees";
import { useAuth, isManager } from "@/context/AuthContext";

const EMPTY = { title: "", description: "", owner_id: "", target_date: "", progress: 0 };

export default function Goals() {
  const { user } = useAuth();
  const manager = isManager(user);
  const { employees } = useEmployees();
  const [goals, setGoals] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/goals"); setGoals(data); } catch (e) { toast.error(errMsg(e)); setGoals([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (g) => { setF(g ? { ...EMPTY, ...g, owner_id: g.owner_id || "", target_date: g.target_date || "" } : EMPTY); setDialog({ open: true, initial: g }); };
  const editing = !!dialog.initial?.goal_id;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { title: f.title, description: f.description, owner_id: f.owner_id || null, target_date: f.target_date || null, progress: f.progress };
    try {
      if (editing) await api.patch(`/goals/${dialog.initial.goal_id}`, body);
      else await api.post("/goals", body);
      toast.success(editing ? "Goal updated" : "Goal created");
      setDialog({ open: false, initial: null });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    try { await api.delete(`/goals/${dialog.initial.goal_id}`); toast.success("Goal deleted"); setDialog({ open: false, initial: null }); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="goals-page">
      <PageHeader title="Team goals" subtitle="Company objectives, with progress and linked tasks"
        actions={manager && <Button onClick={() => open(null)} data-testid="goals-new-button"><Plus className="h-4 w-4 mr-1.5" />New goal</Button>} />
      {goals === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        goals.length === 0 ? <div className="bg-card border border-border rounded-xl"><EmptyState icon={Target} title="No goals yet" text="Set a goal and link tasks to it to track progress." /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="goals-list">
            {goals.map((g) => (
              <div key={g.goal_id} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col" data-testid="goal-card">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold leading-snug">{g.title}</p>
                  <StatusBadge status={g.status} />
                </div>
                {g.description && <p className="text-sm text-muted-foreground mt-1.5">{g.description}</p>}
                <div className="mt-4" data-testid="goal-progress">
                  <div className="flex justify-between text-xs mb-1.5"><span className="text-muted-foreground">Progress</span><span className="font-semibold tabular-nums">{g.progress}%</span></div>
                  <Progress value={g.progress} className="h-2" />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                  {g.owner_name && <span className="flex items-center gap-1.5"><PersonAvatar name={g.owner_name} className="h-5 w-5" />{g.owner_name}</span>}
                  {g.target_date && <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{fmtDate(g.target_date, { month: "short", day: "numeric", year: "numeric" })}</span>}
                  <span className="flex items-center gap-1"><ListTodo className="h-3.5 w-3.5" />{g.task_done}/{g.task_total} tasks</span>
                </div>
                {manager && <Button variant="secondary" size="sm" className="mt-4 self-start" onClick={() => open(g)} data-testid="goal-open-button">Update</Button>}
              </div>
            ))}
          </div>
        )}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="bg-card max-w-lg" data-testid="goal-dialog">
          <DialogHeader><DialogTitle>{editing ? "Update goal" : "New goal"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Goal</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="goal-title-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="goal-description-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Owner</Label>
                <Select value={f.owner_id || "none"} onValueChange={(v) => setF({ ...f, owner_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="goal-owner-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No owner</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Target date</Label><DatePicker value={f.target_date} onChange={(v) => setF({ ...f, target_date: v })} allowClear testid="goal-target-date-picker" /></div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between"><Label>Progress</Label><span className="text-sm font-semibold tabular-nums" data-testid="goal-progress-value">{f.progress}%</span></div>
              <Slider value={[f.progress]} min={0} max={100} step={5} onValueChange={([v]) => setF({ ...f, progress: v })} data-testid="goal-progress-slider" />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              {editing && <Button type="button" variant="ghost" className="text-destructive mr-auto" onClick={remove} data-testid="goal-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
              <Button type="submit" disabled={busy} data-testid="goal-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{editing ? "Save" : "Create goal"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
